import { useCallback, useEffect, useState } from 'react';
import {
    Box, Checkbox, Chip, FormControlLabel, IconButton, Skeleton, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { environment } from 'routes/environment.router';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { ApiToken, ApiTokenService } from '../services/api-token.service';
import { ApiTokenRow } from './api-token-row';
import {
    API_SCOPES, SCOPE_GROUPS, SCOPE_PRESETS, needsBoards,
} from '../api-token-scopes';

/// Chaves de API de quem está logado: gerar, ver o uso e revogar; a inteira só aparece ao gerar.
export const ApiTokenPanel = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    const [list, setList] = useState<ApiToken[] | null>(null);
    const [name, setName] = useState('');
    /// Começa vazio: a pessoa escolhe o que a chave pode, em vez de desmarcar o excesso.
    const [scopes, setScopes] = useState<string[]>([]);
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState<string | null>(null);

    const load = useCallback(() => {
        new ApiTokenService()
            .list()
            .then((res) => setList(res.data.data ?? []))
            .catch(() => setList([]));
    }, []);

    useEffect(load, [load]);

    const canCreate = Boolean(name.trim()) && scopes.length > 0 && !creating;

    const toggleScope = (scope: string) =>
        setScopes((current) =>
            (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));

    const create = async () => {
        if (!canCreate) return;

        setCreating(true);

        try {
            const res = await new ApiTokenService().create(name.trim(), scopes);
            setCreated(res.data.data.token);
            setName('');
            setScopes([]);
            load();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setCreating(false);
        }
    };

    const revoke = (token: ApiToken) =>
        alertDelete(token.id, async () => {
            try {
                await new ApiTokenService().revoke(token.id);
                load();
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        }, token.name);

    const copy = (text: string) =>
        navigator.clipboard.writeText(text).then(() => showAlert(t('apiTokens.copiado'), 'success', 1500));

    const base = (environment.api_url ?? '').replace(/\/$/, '');
    const example = `curl -H "Authorization: Bearer ${created ?? 'SUA_CHAVE'}" ${base}/crm/leads/options`;

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
    };

    const section = {
        p: 2,
        border: `1px solid ${cc.border}`,
        borderRadius: '10px',
        bgcolor: cc.countBg,
    };

    const code = {
        fontFamily: 'monospace',
        fontSize: 12.5,
        color: cc.textPrimary,
        background: cc.surfaceBg,
        border: `1px solid ${cc.border}`,
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        wordBreak: 'break-all' as const,
        flex: 1,
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
            <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                {t('apiTokens.ajuda')}
            </Typography>

            {created && (
                <Box sx={{ border: `1px solid ${cc.accent}`, borderRadius: '10px', p: 2, background: cc.accentSoftBg }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: cc.accent }}>
                        {t('apiTokens.geradaTitulo')}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: cc.textSecondary, mt: 0.5, mb: 1.25 }}>
                        {t('apiTokens.geradaAjuda')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Box sx={code}>{created}</Box>
                        <Tooltip title={t('apiTokens.copiar')}>
                            <IconButton onClick={() => copy(created)} sx={{ color: cc.accent }}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {/* Chaves existentes primeiro: é o que se procura ao abrir; gerar é mais raro. */}
            {!list ? (
                <Skeleton variant="rounded" height={88} />
            ) : list.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', border: `1px dashed ${cc.borderStrong}`, borderRadius: '10px' }}>
                    <Typography sx={{ fontSize: 13, color: cc.textMuted }}>
                        {t('apiTokens.nenhuma')}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
                    {list.map((token) => (
                        <ApiTokenRow key={token.id} token={token} onRevoke={() => revoke(token)} />
                    ))}
                </Box>
            )}

            <Box sx={section}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary, mb: 1 }}>
                    {t('apiTokens.gerar')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && create()}
                        placeholder={t('apiTokens.nomePlaceholder')}
                        inputProps={{ maxLength: 80 }}
                        sx={field}
                    />
                    <MDButton
                        variant="gradient"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={create}
                        disabled={!canCreate}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {t('apiTokens.gerarBotao')}
                    </MDButton>
                </Box>

                <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary, flex: 1 }}>
                            {t('apiTokens.permissoes')}
                        </Typography>

                        <Typography sx={{ fontSize: 12, color: cc.textMuted }}>
                            {t('apiTokens.atalhos')}
                        </Typography>
                        {SCOPE_PRESETS.map((preset) => (
                            <Chip
                                key={preset.labelKey}
                                label={t(preset.labelKey)}
                                size="small"
                                variant="outlined"
                                onClick={() => setScopes([...preset.scopes])}
                                sx={{ '& .MuiChip-label': { color: cc.onTint } }}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(230px, 1fr))' },
                            gap: 2,
                        }}
                    >
                        {SCOPE_GROUPS.map((group) => (
                            <Box key={group.titleKey}>
                                <Typography
                                    sx={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        letterSpacing: '.04em',
                                        textTransform: 'uppercase',
                                        color: cc.textMuted,
                                        mb: 0.25,
                                    }}
                                >
                                    {t(group.titleKey)}
                                </Typography>

                                {group.scopes.map((key) => (
                                    <FormControlLabel
                                        key={key}
                                        sx={{ display: 'flex', ml: -0.75, mr: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={scopes.includes(API_SCOPES[key])}
                                                onChange={() => toggleScope(API_SCOPES[key])}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: 13, color: cc.textPrimary }}>
                                                {t(`apiTokens.escopos.${key}`)}
                                            </Typography>
                                        }
                                    />
                                ))}

                                {group.scopes.includes('messagesSend') && (
                                    <Typography sx={{ fontSize: 11.5, color: cc.eventWarning, mt: 0.25 }}>
                                        {t('apiTokens.mensagensAjuda')}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {needsBoards(scopes) && (
                        <Typography sx={{ fontSize: 12, color: cc.eventWarning, mt: 1.5 }}>
                            {t('apiTokens.precisaQuadros')}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box sx={section}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary, mb: 0.5 }}>
                    {t('apiTokens.comoUsar')}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: cc.textSecondary, mb: 1 }}>
                    {t('apiTokens.comoUsarAjuda')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={code}>{example}</Box>
                    <Tooltip title={t('apiTokens.copiar')}>
                        <IconButton onClick={() => copy(example)} sx={{ color: cc.textSecondary }}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography sx={{ fontSize: 12, color: cc.textMuted, mt: 1 }}>
                    {t('apiTokens.documentacao', { url: base.replace(/\/api\/v1$/, '') + '/swagger' })}
                </Typography>
            </Box>
        </Box>
    );
};
